
import { useQuery, useMutation, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionPlan, mapDbPlanToSubscriptionPlan } from './types';
import { Json } from '@/integrations/supabase/types';
import { queryClient } from './queryClient';

// Fetch all subscription plans
export const fetchSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .order('monthly_price');

  if (error) {
    console.error('Error fetching subscription plans:', error);
    throw error;
  }

  return data.map(plan => mapDbPlanToSubscriptionPlan(plan));
};

// Fetch only active subscription plans
export const fetchActiveSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('is_active', true)
    .order('monthly_price');

  if (error) {
    console.error('Error fetching active subscription plans:', error);
    throw error;
  }

  return data.map(plan => mapDbPlanToSubscriptionPlan(plan));
};

// Fetch a specific subscription plan by ID
export const fetchSubscriptionPlanById = async (planId: string): Promise<SubscriptionPlan | null> => {
  if (!planId) return null;
  
  const { data, error } = await supabase
    .from('subscription_plans')
    .select('*')
    .eq('id', planId)
    .single();
    
  if (error) {
    console.error('Error fetching subscription plan by ID:', error);
    throw error;
  }
  
  if (!data) return null;
  
  return mapDbPlanToSubscriptionPlan(data);
};

const normalizePlanFeatures = (
  features: Partial<SubscriptionPlan['features']> | undefined,
  limits?: {
    max_contracts?: number | null;
    max_users?: number | null;
    max_erp_integrations?: number;
    monthly_doc_extractions?: number;
    storage_gb?: number;
  }
) => ({
  currency_conversion: !!features?.currency_conversion,
  multiple_currencies: !!features?.multiple_currencies,
  price_comparison: !!features?.price_comparison,
  grouped_contracts: !!features?.grouped_contracts,
  contract_services: !!features?.contract_services,
  reminders: !!features?.reminders,
  payment_type_selection: !!features?.payment_type_selection,
  dashboard: !!features?.dashboard,
  contract_pdf_email: !!features?.contract_pdf_email,
  document_extraction: !!features?.document_extraction,
  erp_integration: !!features?.erp_integration,
  custom_fields: !!features?.custom_fields,
  calendar_view: !!features?.calendar_view,
  contract_notes: !!features?.contract_notes,
  duplicate_detection: !!features?.duplicate_detection,
  api_read: !!features?.api_read,
  api_write: !!features?.api_write,
  advanced_dashboard: !!features?.advanced_dashboard,
  sales_analytics: !!features?.sales_analytics,
  cost_optimization: !!features?.cost_optimization,
  data_health: !!features?.data_health,
  governing_documents: !!features?.governing_documents,
  max_contracts: limits?.max_contracts ?? features?.max_contracts ?? null,
  max_users: limits?.max_users ?? features?.max_users ?? null,
  max_erp_integrations: limits?.max_erp_integrations ?? features?.max_erp_integrations ?? 0,
  monthly_doc_extractions: limits?.monthly_doc_extractions ?? features?.monthly_doc_extractions ?? 0,
  storage_gb: limits?.storage_gb ?? features?.storage_gb ?? 1,
});

// Create a new subscription plan
export const createSubscriptionPlan = async (planData: Partial<SubscriptionPlan> & { 
  max_contracts?: number | null;
  max_users?: number | null;
  max_erp_integrations?: number;
  monthly_doc_extractions?: number;
  storage_gb?: number;
}): Promise<SubscriptionPlan> => {
  const normalizedFeatures = normalizePlanFeatures(planData.features, {
    max_contracts: planData.max_contracts,
    max_users: planData.max_users,
    max_erp_integrations: planData.max_erp_integrations,
    monthly_doc_extractions: planData.monthly_doc_extractions,
    storage_gb: planData.storage_gb,
  });

  const { data, error } = await supabase
    .from('subscription_plans')
    .insert({
      name: planData.name,
      description: planData.description || null,
      plan_type: planData.plan_type,
      monthly_price: planData.monthly_price,
      annual_price: planData.annual_price || null,
      features: normalizedFeatures as unknown as Json,
      is_active: planData.is_active ?? true,
      show_on_landing: planData.show_on_landing ?? false,
      max_contracts: normalizedFeatures.max_contracts,
      max_users: normalizedFeatures.max_users,
      max_erp_integrations: normalizedFeatures.max_erp_integrations,
      monthly_doc_extractions: normalizedFeatures.monthly_doc_extractions,
      storage_gb: normalizedFeatures.storage_gb,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating subscription plan:', error);
    throw error;
  }

  return mapDbPlanToSubscriptionPlan(data);
};

// Update an existing subscription plan
export const updateSubscriptionPlan = async (plan: SubscriptionPlan & {
  max_contracts?: number | null;
  max_users?: number | null;
  max_erp_integrations?: number;
  monthly_doc_extractions?: number;
  storage_gb?: number;
}): Promise<SubscriptionPlan> => {
  const normalizedFeatures = normalizePlanFeatures(plan.features, {
    max_contracts: plan.max_contracts,
    max_users: plan.max_users,
    max_erp_integrations: plan.max_erp_integrations,
    monthly_doc_extractions: plan.monthly_doc_extractions,
    storage_gb: plan.storage_gb,
  });

  const { data, error } = await supabase
    .from('subscription_plans')
    .update({
      name: plan.name,
      description: plan.description || null,
      plan_type: plan.plan_type,
      monthly_price: plan.monthly_price,
      annual_price: plan.annual_price || null,
      features: normalizedFeatures as unknown as Json,
      is_active: plan.is_active,
      show_on_landing: plan.show_on_landing,
      max_contracts: normalizedFeatures.max_contracts,
      max_users: normalizedFeatures.max_users,
      max_erp_integrations: normalizedFeatures.max_erp_integrations,
      monthly_doc_extractions: normalizedFeatures.monthly_doc_extractions,
      storage_gb: normalizedFeatures.storage_gb,
      updated_at: new Date().toISOString()
    })
    .eq('id', plan.id)
    .select()
    .single();

  if (error) {
    console.error('Error updating subscription plan:', error);
    throw error;
  }

  return mapDbPlanToSubscriptionPlan(data);
};

// Delete a subscription plan
export const deleteSubscriptionPlan = async (planId: string): Promise<void> => {
  const { error } = await supabase
    .from('subscription_plans')
    .delete()
    .eq('id', planId);

  if (error) {
    console.error('Error deleting subscription plan:', error);
    throw error;
  }
};

// React Query hooks for plans
export const usePlanHooks = () => {
  // Query hooks
  const useSubscriptionPlansQuery = () => 
    useQuery({
      queryKey: ['subscription-plans'],
      queryFn: fetchSubscriptionPlans
    });

  const useActiveSubscriptionPlansQuery = () => 
    useQuery({
      queryKey: ['active-subscription-plans'],
      queryFn: fetchActiveSubscriptionPlans
    });

  const useSubscriptionPlanByIdQuery = (planId: string | undefined) => 
    useQuery({
      queryKey: ['subscription-plan', planId],
      queryFn: () => fetchSubscriptionPlanById(planId!),
      enabled: !!planId
    });

  // Mutation hooks
  const useCreateSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: createSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
      }
    });

  const useUpdateSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: updateSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['subscription-plan'] });
      }
    });

  const useDeleteSubscriptionPlanMutation = () => 
    useMutation({
      mutationFn: deleteSubscriptionPlan,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
        queryClient.invalidateQueries({ queryKey: ['active-subscription-plans'] });
      }
    });

  return {
    useSubscriptionPlansQuery,
    useActiveSubscriptionPlansQuery,
    useSubscriptionPlanByIdQuery,
    useCreateSubscriptionPlanMutation,
    useUpdateSubscriptionPlanMutation,
    useDeleteSubscriptionPlanMutation
  };
};
